import { useMemo } from 'react';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { Pie } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip } from '@visx/tooltip';
import { seriesColor, CHART_THEME } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';
import type { LabeledValue } from '../../api/types';

interface Props {
  data: LabeledValue[];
  title?: string;
  loading?: boolean;
}

const SIZE = 220;
const THICKNESS = 40;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    marginBottom: 4,
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 2,
    flexShrink: 0,
  },
  legendValue: {
    fontWeight: 500,
    marginLeft: 'auto',
    paddingLeft: 12,
  },
});

export const DonutChart = ({ data, loading }: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const colors = CHART_THEME[mode];

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip<{ title: string; items: { color: string; label: string; value: string }[] }>();

  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={SIZE}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!data.length || total === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={SIZE}>
        <Typography variant="body2" style={{ opacity: 0.5 }}>No data</Typography>
      </Box>
    );
  }

  const radius = SIZE / 2;
  const innerRadius = radius - THICKNESS;

  return (
    <Box position="relative" className={classes.root}>
      <svg width={SIZE} height={SIZE}>
        <Group top={radius} left={radius}>
          <Pie
            data={data}
            pieValue={d => d.value}
            outerRadius={radius - 4}
            innerRadius={innerRadius}
            padAngle={0.02}
            cornerRadius={3}
          >
            {pie =>
              pie.arcs.map((arc, i) => {
                const [cx, cy] = pie.path.centroid(arc);
                const pct = ((arc.data.value / total) * 100).toFixed(1);
                return (
                  <g
                    key={`arc-${i}`}
                    onMouseMove={e => {
                      showTooltip({
                        tooltipLeft: e.clientX,
                        tooltipTop: e.clientY,
                        tooltipData: {
                          title: arc.data.label,
                          items: [{
                            color: seriesColor(i),
                            label: 'Count',
                            value: `${arc.data.value.toLocaleString()} (${pct}%)`,
                          }],
                        },
                      });
                    }}
                    onMouseLeave={() => hideTooltip()}
                  >
                    <path
                      d={pie.path(arc) ?? ''}
                      fill={seriesColor(i)}
                      opacity={0.85}
                      style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                    />
                    {/* Percentage label on arc if there's enough space */}
                    {arc.endAngle - arc.startAngle > 0.3 && (
                      <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill={colors.tooltipText}
                        fontSize={11}
                        fontWeight={500}
                        pointerEvents="none"
                      >
                        {pct}%
                      </text>
                    )}
                  </g>
                );
              })
            }
          </Pie>
        </Group>
      </svg>

      {/* Legend */}
      <Box>
        {data.map((d, i) => {
          const pct = ((d.value / total) * 100).toFixed(1);
          return (
            <div key={i} className={classes.legendItem} style={{ color: colors.tooltipText }}>
              <span className={classes.swatch} style={{ backgroundColor: seriesColor(i) }} />
              <span>{d.label}</span>
              <span className={classes.legendValue}>{d.value.toLocaleString()} ({pct}%)</span>
            </div>
          );
        })}
      </Box>

      {tooltipOpen && tooltipData && (
        <ChartTooltip
          top={(tooltipTop ?? 0) - 10}
          left={(tooltipLeft ?? 0) + 10}
          data={tooltipData}
        />
      )}
    </Box>
  );
};
